import { ReactElement, useState } from 'react'
import { round } from 'lodash-es'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Tab, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import useLoadsCombinedStore from '@editor/pages/Results2/stores/useLoadsCombinedStore'
import type { DesignForcesOnSupportDTO } from 'src/state/api/generated/fastAPI.schemas'
import LoadResultsGrid from '../../LoadResultsTable'
import {
  categoryColumn,
  combineRowsBy,
  mergeOrigins,
  numberColumn,
  sourceColumn,
  textColumn,
  WALL_LOAD_POSITION_DESCRIPTION,
} from '../../LoadResultsTable/columns'
import useLoadVisibilityColumn from '../../LoadResultsTable/useLoadVisibilityColumn'
import { anchorLoadGuid, formatLoadCase, formatLoadCategoryShort } from '../utils'

type AnchorLoadTab = 'horizontal' | 'above' | 'point' | 'line'

interface Props {
  designForces?: DesignForcesOnSupportDTO
  isLoading: boolean
}

const faColumn = numberColumn(
  'F_a',
  <span>
    F<sub>a</sub>
  </span>,
  'Fa',
  'kN',
)

/**
 * Einwirkungen of one tensile anchor in the layout of the existing load tables (tab strip +
 * compact data grid with sortable columns and header filters), fed from the per-support design
 * forces: the export's horizontal head loads, tensile loads from above, and relieving point/line
 * loads — each with this anchor's share (Fa) in kN.
 */
const AnchorLoadResults = ({ designForces, isLoading }: Props): ReactElement => {
  const [tab, setTab] = useState<AnchorLoadTab>('horizontal')
  const loadsCombined = useLoadsCombinedStore(state => state.loadsCombined)
  const visibilityColumn = useLoadVisibilityColumn()

  if (!designForces) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        {isLoading ? 'Einwirkungen werden geladen …' : 'Keine Einwirkungen verfügbar'}
      </Typography>
    )
  }

  const horizontalColumns: GridColDef[] = [
    textColumn('loadCase', 'Lastfall', 1, 80),
    categoryColumn,
    numberColumn(
      'F_hk',
      <span>
        F<sub>h,k</sub>
      </span>,
      'Fh,k',
      'kN',
    ),
    numberColumn(
      'F_a',
      <span>
        F<sub>a</sub>
      </span>,
      'Fa',
      'kN',
      110,
    ),
  ]
  const horizontalRows = designForces.tensile_from_horizontal_loads.map((load, index) => ({
    id: index,
    loadGuids: [anchorLoadGuid(load.original_load, designForces.support_guid, 'horizontal', index)],
    loadCase: formatLoadCase(load.original_load.load_case, load.load_case_type),
    category: formatLoadCategoryShort(load.original_load.load_case),
    F_hk: load.f_x / 1000,
    F_a: load.anchor_force / 1000,
  }))
  const horizontalRowsDisplayed = loadsCombined
    ? combineRowsBy(
        horizontalRows,
        row => row.loadCase,
        (a, b) => ({
          ...a,
          loadGuids: [...a.loadGuids, ...b.loadGuids],
          F_hk: a.F_hk + b.F_hk,
          F_a: a.F_a + b.F_a,
        }),
      )
    : horizontalRows

  const pointColumns = (forceLabel: ReactElement, forcePlainLabel: string): GridColDef[] => [
    sourceColumn,
    textColumn('loadCase', 'Lastfall'),
    categoryColumn,
    numberColumn('pos', 'Position', 'Position', 'm'),
    numberColumn('force', forceLabel, forcePlainLabel, 'kN'),
    faColumn,
  ]
  const toPointRow = (
    load: DesignForcesOnSupportDTO['tensile_from_elements_above'][number],
    index: number,
    // The guid kind must match `getReducingDomainLoads` so the eye column controls the scene.
    kind: string,
  ) => ({
    id: index,
    loadGuids: [anchorLoadGuid(load.original_load, designForces.support_guid, kind, index)],
    origin: load.origin_label && load.origin_label !== '-' ? load.origin_label : 'extern',
    loadCase: formatLoadCase(load.original_load.load_case, load.load_case_type),
    category: formatLoadCategoryShort(load.original_load.load_case),
    pos: load.pos,
    force: load.f_z / 1000,
    F_a: load.anchor_force / 1000,
  })
  const aboveRows = designForces.tensile_from_elements_above.map((load, index) =>
    toPointRow(load, index, 'tensile-above'),
  )
  const pointRows = designForces.reducing_point_loads.map((load, index) =>
    toPointRow(load, index, 'reducing-point'),
  )
  const combinePointRows = (rows: typeof pointRows) =>
    combineRowsBy(
      rows,
      row => `${row.loadCase}-${round(row.pos, 2)}`,
      (a, b) => ({
        ...a,
        loadGuids: [...a.loadGuids, ...b.loadGuids],
        origin: mergeOrigins(a.origin, b.origin),
        force: a.force + b.force,
        F_a: a.F_a + b.F_a,
      }),
    )
  const aboveRowsDisplayed = loadsCombined ? combinePointRows(aboveRows) : aboveRows
  const pointRowsDisplayed = loadsCombined ? combinePointRows(pointRows) : pointRows

  const lineColumns: GridColDef[] = [
    sourceColumn,
    textColumn('loadCase', 'Lastfall'),
    categoryColumn,
    numberColumn('start', 'Start', 'Start', 'm'),
    numberColumn('end', 'Ende', 'Ende', 'm'),
    numberColumn(
      'q_za',
      <span>
        q<sub>z,a</sub>
      </span>,
      'qz,a',
      'kN/m',
    ),
    numberColumn(
      'q_ze',
      <span>
        q<sub>z,e</sub>
      </span>,
      'qz,e',
      'kN/m',
    ),
    faColumn,
  ]
  const lineRows = designForces.reducing_line_loads.map((load, index) => ({
    id: index,
    loadGuids: [
      anchorLoadGuid(load.original_load, designForces.support_guid, 'reducing-line', index),
    ],
    origin: load.origin_label && load.origin_label !== '-' ? load.origin_label : 'extern',
    loadCase: formatLoadCase(load.original_load.load_case, load.load_case_type),
    category: formatLoadCategoryShort(load.original_load.load_case),
    start: load.pos_start,
    end: load.pos_end,
    q_za: load.q_z_start / 1000,
    q_ze: load.q_z_end / 1000,
    F_a: load.anchor_force / 1000,
  }))
  const lineRowsDisplayed = loadsCombined
    ? combineRowsBy(
        lineRows,
        row => `${row.loadCase}-${round(row.start, 2)}-${round(row.end, 2)}`,
        (a, b) => ({
          ...a,
          loadGuids: [...a.loadGuids, ...b.loadGuids],
          origin: mergeOrigins(a.origin, b.origin),
          q_za: a.q_za + b.q_za,
          q_ze: a.q_ze + b.q_ze,
          F_a: a.F_a + b.F_a,
        }),
      )
    : lineRows

  const tabPanelSx = { px: 0, pt: 1, pb: 0 } as const

  return (
    <Box sx={{ pb: 1 }}>
      <TabContext value={tab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: 12,
              paddingX: ({ spacing }) => spacing(1.5),
            },
          }}
        >
          <TabList scrollButtons="auto" variant="scrollable" onChange={(_, value) => setTab(value)}>
            <Tab value="horizontal" label="Kopflasten" />
            <Tab value="above" label="Zuglasten von oben" />
            <Tab value="point" label="Punkt-Auflasten" />
            <Tab value="line" label="Linien-Auflasten" />
          </TabList>
        </Box>
        <TabPanel value="horizontal" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[visibilityColumn(horizontalRowsDisplayed), ...horizontalColumns]}
            rows={horizontalRowsDisplayed}
            emptyMessage="Keine horizontalen Kopflasten"
          />
        </TabPanel>
        <TabPanel value="above" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[
              visibilityColumn(aboveRowsDisplayed),
              ...pointColumns(
                <span>
                  F<sub>t,k</sub>
                </span>,
                'Ft,k',
              ),
            ]}
            rows={aboveRowsDisplayed}
            emptyMessage="Keine Zuglasten von oben"
          />
        </TabPanel>
        <TabPanel value="point" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[
              visibilityColumn(pointRowsDisplayed),
              ...pointColumns(
                <span>
                  F<sub>z</sub>
                </span>,
                'Fz',
              ),
            ]}
            rows={pointRowsDisplayed}
            emptyMessage="Keine Punkt-Auflasten"
            description={WALL_LOAD_POSITION_DESCRIPTION}
          />
        </TabPanel>
        <TabPanel value="line" sx={tabPanelSx}>
          <LoadResultsGrid
            columns={[visibilityColumn(lineRowsDisplayed), ...lineColumns]}
            rows={lineRowsDisplayed}
            emptyMessage="Keine Linien-Auflasten"
            description={WALL_LOAD_POSITION_DESCRIPTION}
          />
        </TabPanel>
      </TabContext>
    </Box>
  )
}

export default AnchorLoadResults
